/**
 * キャリコネクションの①②に送るスナップショット（basicData・履歴書・職務経歴書）を決める。
 *
 * - 書式データを受け取らない呼び出し（RT1.0 ダイアログ・②）は、先行フェッチと同じ画面上の値
 *   （AUTO_RT の最新ペイロード）を使う。保存値を読むと指紋がずれて①が余分に飛ぶ
 * - 画面に無い側の書類は保存値で補う。履歴書の出力中は職務経歴書とスキルシートのどちらを送るかを
 *   保存値の選択（careerconnectionStorage）に従う（画面の相手書式は常に通常の職務経歴書のため）
 * - 保存値は output 1 回の間は変わらないので書式 ID ごとに 1 件覚え、2 回目以降は同期で決める
 *   （DL バナーが現在の指紋で①の結果を照合できるように）。output 離脱で捨てる
 */
import { getCcAutoRtArgs } from '@/composables/useCareerconnectionAutoRtArgs'
import { expectedDocumentTypeForFormat } from '@/utils/format-edit/formatDetailBackup'
import {
  loadFormatsFromStorage,
  type CcStoredFormats
} from '@/utils/realtime-transfer/careerconnectionStorage'
import {
  computeSnapshotFingerprint,
  splitFormatDataByFormatId,
  type CcFormats
} from '@/utils/realtime-transfer/careerconnectionSnapshot'

type UnknownRecord = Record<string, any>

export interface CcSnapshotContext {
  formatId: string
  basicData: UnknownRecord
  formatData?: unknown
  otherFormatData?: unknown
}

let storedCache: { formatId: string; stored: CcStoredFormats } | null = null

function sourceOf(ctx: CcSnapshotContext): CcSnapshotContext {
  if (ctx.formatData !== undefined) return ctx
  const args = getCcAutoRtArgs()
  if (args && args[2] === ctx.formatId) {
    return {
      ...ctx,
      basicData: args[1] || ctx.basicData,
      formatData: args[3],
      otherFormatData: args[4]
    }
  }
  return ctx
}

const isShokumuContext = (formatId: string) =>
  expectedDocumentTypeForFormat(formatId) === 'shokumukeirekisho'

function compose(
  ctx: CcSnapshotContext,
  stored: CcStoredFormats | null
): CcFormats {
  const src = sourceOf(ctx)
  const split = splitFormatDataByFormatId(
    ctx.formatId,
    src.formatData,
    src.otherFormatData
  )
  const storedShokumu = stored ? stored.formatShokumukeirekisho : null
  const useStoredShokumu =
    !isShokumuContext(ctx.formatId) &&
    !!stored &&
    stored.shokumuSource === 'skillsheet'
  return {
    basicData: src.basicData || {},
    formatRirekisho:
      split.formatRirekisho || (stored ? stored.formatRirekisho : null),
    formatShokumukeirekisho: useStoredShokumu
      ? storedShokumu
      : split.formatShokumukeirekisho || storedShokumu
  }
}

/** 同期で決まるなら返す（保存値の読み込みが要るときは null） */
export function resolveSnapshotSync(ctx: CcSnapshotContext): CcFormats | null {
  // 保存値の記憶は出力画面の間だけ有効（AUTO_RT の引数がある間）。マイページのように
  // 出力画面を経ない経路では毎回読み直す（保存済みの書類を直したあとに古い内容で送らない）
  if (
    storedCache &&
    storedCache.formatId === ctx.formatId &&
    getCcAutoRtArgs()
  ) {
    return compose(ctx, storedCache.stored)
  }
  if (!isShokumuContext(ctx.formatId)) return null
  const src = sourceOf(ctx)
  const split = splitFormatDataByFormatId(
    ctx.formatId,
    src.formatData,
    src.otherFormatData
  )
  // 職務経歴書の出力中で両方の書類が画面にあれば保存値は要らない
  return split.formatShokumukeirekisho && split.formatRirekisho
    ? compose(ctx, null)
    : null
}

export async function resolveSnapshot(
  ctx: CcSnapshotContext
): Promise<CcFormats> {
  const stored = await loadFormatsFromStorage(undefined, ctx.formatId)
  storedCache = { formatId: ctx.formatId, stored }
  return compose(ctx, stored)
}

export const snapshotFingerprint = (snapshot: CcFormats): string =>
  computeSnapshotFingerprint(
    snapshot.basicData,
    snapshot.formatRirekisho,
    snapshot.formatShokumukeirekisho
  )

/** 今の画面の値での指紋。同期で決まらなければ null（呼び出し側は結果を使わない） */
export function currentSnapshotFingerprint(
  ctx: CcSnapshotContext
): string | null {
  const snapshot = resolveSnapshotSync(ctx)
  return snapshot ? snapshotFingerprint(snapshot) : null
}

export function clearCcSnapshotStoredCache(): void {
  storedCache = null
}
